// Picking a model: each option with its price (per 5 s for video, or for this scene), what it's good at and what
// it won't do ("rejects realistic human faces"). Used per scene, and for the project and account defaults.
import { useEffect, useState } from "react";
import { api } from "../api";
import { creditsFor, priceDelta, priceLabel } from "../models";
import type { ImageShowcaseData } from "../showcaseImages";
import type { ModelInfo, ModelStep } from "../types";
import { Icon } from "./bits";
import { StillStrip } from "./ImageShowcase";

let catalog: Promise<ModelInfo[]> | null = null;
/** The model catalog (GET /api/models), loaded once. */
export function useModels(): ModelInfo[] | null {
  const [list, setList] = useState<ModelInfo[] | null>(null);
  useEffect(() => {
    let live = true;
    catalog ??= api.models().catch((e) => {
      catalog = null;
      throw e;
    });
    catalog.then((m) => live && setList(m)).catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, []);
  return list;
}

export function ModelOptions({
  step,
  models,
  value,
  onPick,
  durationS,
  busy = false,
  dataAction,
  stills,
  lipSyncIds,
}: {
  step: ModelStep;
  models: ModelInfo[];
  value: string | null;
  onPick: (m: ModelInfo) => void;
  /** a clip's length: prices are shown for it, with the difference from the current pick */
  durationS?: number;
  busy?: boolean;
  /** v4 (D6): the parity id each option carries (web/src/parity.ts) */
  dataAction?: string;
  /** MS-IMG: picture models show their showcase stills (the `row` style first) under each option */
  stills?: { data: ImageShowcaseData | null; row?: string | null };
  /** lip sync (2026-10-30): a talking scene's best lip-sync models, best first: badged and listed first */
  lipSyncIds?: string[];
}) {
  const lip = lipSyncIds ?? [];
  const rank = (id: string) => (lip.includes(id) ? lip.indexOf(id) : lip.length);
  const options = models.filter((m) => m.step === step).sort((a, b) => rank(a.id) - rank(b.id));
  const current = options.find((m) => m.id === value);
  if (!options.length) return <p className="muted">No models to choose from yet.</p>;
  return (
    <ul className="models" role="radiogroup" aria-label="Model">
      {options.map((m) => {
        const on = m.id === value;
        return (
          <li key={m.id}>
            <button type="button" role="radio" aria-checked={on} className={`model${on ? " is-on" : ""}`} data-action={dataAction} disabled={busy} onClick={() => !on && onPick(m)}>
              <span className="model-top">
                <span className="model-name">{m.label}</span>
                {m.isDefault && <span className="tag">Default</span>}
                {lip.includes(m.id) && <span className="tag tag-good">Good for lip sync</span>}
                {m.rateSource === "unverified" && <span className="tag tag-warn">Rate unverified</span>}
                <span className="model-price">
                  {durationS != null && step === "clip" ? `${creditsFor(m, durationS)} credits` : priceLabel(m)}
                  {durationS != null && step === "clip" && !on && current && <small>{priceDelta(current, m, durationS)}</small>}
                </span>
              </span>
              {durationS != null && step === "clip" && <span className="model-sub">{priceLabel(m)}</span>}
              {!!m.strengths.length && <span className="model-strengths">{m.strengths.join(", ")}</span>}
              {m.constraints.map((c) => (
                <span key={c} className="model-constraint">
                  <Icon name="alert" size={14} /> {c.replace(/^./, (x) => x.toUpperCase())}
                </span>
              ))}
              {on && (
                <span className="model-check" aria-hidden>
                  <Icon name="check" size={16} />
                </span>
              )}
            </button>
            {step === "image" && stills?.data && <StillStrip data={stills.data} modelId={m.id} label={m.label} row={stills.row} max={4} price={false} />}
          </li>
        );
      })}
    </ul>
  );
}
